import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import type { PhotoResponse } from "@egalregal/shared";
import { ChevronRightIcon } from "../../shared/components/icons";
import { useT } from "../../shared/i18n";
import { PhotoLightbox } from "./PhotoLightbox";
import { photoFull } from "./photoUrls";

interface GalleryLightboxProps {
  open: boolean;
  photos: PhotoResponse[];
  /** Index into `photos` of the photo to show, or null when closed. */
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  onOpenContainer: (containerId: string) => void;
  onOpenItem: (itemId: string) => void;
}

/**
 * Full-screen gallery lightbox (matching the container view's photo lightbox):
 * original photo, prev/next, zoom, swipe — plus a footer that names the
 * container or item behind the photo with a link into its single view.
 *
 * Composes the shared PhotoLightbox core; the gallery-only footer slots in.
 */
export function GalleryLightbox({
  open,
  photos,
  index,
  onClose,
  onIndexChange,
  onOpenContainer,
  onOpenItem,
}: GalleryLightboxProps) {
  const t = useT();

  const typeLabel = (type?: "general" | "nutrition" | string) =>
    type === "nutrition" ? t("galleryFilterNutrition") : t("galleryFilterGeneral");

  const titleFor = (photo: PhotoResponse | undefined) => {
    if (!photo) return "";
    return photo.itemId
      ? (photo.itemName ?? typeLabel(photo.itemPhotoType))
      : (photo.containerName ?? photo.containerPath ?? "");
  };

  const photosForCore = photos.map((photo) => ({
    id: photo.photoId ?? photo.file?.id ?? photo.itemId ?? photo.containerId ?? "",
    name: titleFor(photo),
    src: photoFull(photo),
  }));

  const photo = index !== null ? photos[index] : undefined;
  const title = titleFor(photo);
  const subtitle = photo?.itemId
    ? typeLabel(photo.itemPhotoType)
    : (photo?.file?.name ?? "");

  return (
    <PhotoLightbox
      open={open}
      photos={photosForCore}
      index={index ?? 0}
      onClose={onClose}
      onIndexChange={onIndexChange}
      testId="gallery-lightbox"
      imageTestId="gallery-lightbox-image"
      headerText={index !== null ? `${index + 1} / ${photos.length}` : ""}
      footer={
        <>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {title}
            </Typography>
            <Typography variant="caption" noWrap color="rgba(255,255,255,0.7)">
              {subtitle}
            </Typography>
          </Box>
          {photo?.itemId ? (
            <Button
              variant="contained"
              size="small"
              endIcon={<ChevronRightIcon />}
              onClick={() => onOpenItem(photo.itemId!)}
            >
              {t("goToItem")}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              endIcon={<ChevronRightIcon />}
              disabled={!photo?.containerId}
              onClick={() => photo?.containerId && onOpenContainer(photo.containerId)}
            >
              {t("goToContainer")}
            </Button>
          )}
        </>
      }
    />
  );
}
